// Signal smoothing utilities — pure functions, fully unit-testable.
//
// Two complementary tools:
//   • OneEuroFilter  — low-latency smoothing for *event detection* (keeps timing).
//   • savitzkyGolay  — smoother curves for *feature waveforms* (keeps shape).

/**
 * One-Euro filter (Casiez et al.). Adaptive low-pass: smooths jitter at low
 * speed without lagging fast motion — ideal for landmark trajectories used in
 * heel-strike / toe-off detection.
 */
export class OneEuroFilter {
  private xPrev: number | null = null;
  private dxPrev = 0;
  private tPrev: number | null = null;

  constructor(
    private minCutoff = 1.0,
    private beta = 0.007,
    private dCutoff = 1.0,
  ) {}

  private alpha(cutoff: number, dt: number): number {
    const tau = 1 / (2 * Math.PI * cutoff);
    return 1 / (1 + tau / dt);
  }

  filter(x: number, tMs: number): number {
    if (this.tPrev === null || this.xPrev === null) {
      this.tPrev = tMs;
      this.xPrev = x;
      return x;
    }
    const dt = Math.max((tMs - this.tPrev) / 1000, 1e-4);
    const dx = (x - this.xPrev) / dt;
    const aD = this.alpha(this.dCutoff, dt);
    const dxHat = aD * dx + (1 - aD) * this.dxPrev;
    const cutoff = this.minCutoff + this.beta * Math.abs(dxHat);
    const a = this.alpha(cutoff, dt);
    const xHat = a * x + (1 - a) * this.xPrev;
    this.xPrev = xHat;
    this.dxPrev = dxHat;
    this.tPrev = tMs;
    return xHat;
  }
}

/** Smooth a full series with a fresh One-Euro filter. */
export function oneEuroSeries(
  values: number[],
  tsMs: number[],
  opts?: { minCutoff?: number; beta?: number; dCutoff?: number },
): number[] {
  const f = new OneEuroFilter(opts?.minCutoff, opts?.beta, opts?.dCutoff);
  return values.map((v, i) => f.filter(v, tsMs[i]));
}

/**
 * Savitzky-Golay smoothing (quadratic, symmetric window). Preserves peaks and
 * waveform shape better than a moving average. `window` must be odd; falls back
 * to the input near the edges.
 */
export function savitzkyGolay(values: number[], window = 7): number[] {
  if (window % 2 === 0) window += 1;
  const half = (window - 1) / 2;
  if (values.length < window) return values.slice();

  // Quadratic SG coefficients for a symmetric window (Gram polynomial form).
  const coeffs = sgQuadraticCoeffs(half);
  const out = values.slice();
  for (let i = half; i < values.length - half; i++) {
    let acc = 0;
    for (let k = -half; k <= half; k++) acc += coeffs[k + half] * values[i + k];
    out[i] = acc;
  }
  return out;
}

function sgQuadraticCoeffs(half: number): number[] {
  // Closed-form smoothing coefficients for a quadratic/cubic Savitzky-Golay
  // filter over a symmetric window of half-width m. These provably sum to 1:
  //   C_i = (3(3m² + 3m − 1) − 15 i²) / ((2m+3)(2m+1)(2m−1))
  const m = half;
  const denom = (2 * m + 3) * (2 * m + 1) * (2 * m - 1);
  const c: number[] = [];
  for (let i = -m; i <= m; i++) {
    c.push((3 * (3 * m * m + 3 * m - 1) - 15 * i * i) / denom);
  }
  // Re-normalize as a numerical safety net (sum is 1 analytically).
  const s = c.reduce((a, b) => a + b, 0) || 1;
  return c.map((v) => v / s);
}

/** Coefficient of variation (std/mean) — used for gait-cycle consistency. */
export function coefficientOfVariation(xs: number[]): number {
  if (xs.length < 2) return 0;
  const mean = xs.reduce((a, b) => a + b, 0) / xs.length;
  if (mean === 0) return 0;
  const variance =
    xs.reduce((a, b) => a + (b - mean) * (b - mean), 0) / (xs.length - 1);
  return Math.sqrt(variance) / Math.abs(mean);
}
